import { useState, useEffect } from "react";
import { ref, onValue, push, remove, update, set } from "firebase/database";
import { getAuth } from "firebase/auth";
import { db as database } from "../Firebase/firestore";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { Pencil, Trash2 } from "lucide-react";
import "./broadcast.css";
const BroadcastingPage = () => {
const [users, setUsers] = useState([]);
const [selectedUsers, setSelectedUsers] = useState([]);
const [broadcasts, setBroadcasts] = useState([]);
const [activeTab, setActiveTab] = useState("compose");
const [loading, setLoading] = useState(false);
const [notification, setNotification] = useState({
show: false,
message: "",
type: "",
  });
const [searchTerm, setSearchTerm] = useState("");
// Form states
const [heading, setHeading] = useState("");
const [message, setMessage] = useState("");
const [validity, setValidity] = useState("2 days");
const [recipientType, setRecipientType] = useState("all");
const [editingDraftId, setEditingDraftId] = useState(null);
// NEW: Image upload states
const [imageFile, setImageFile] = useState(null);
const [imagePreview, setImagePreview] = useState("");
const [existingImageUrl, setExistingImageUrl] = useState("");
const [uploadingImage, setUploadingImage] = useState(false);
const auth = getAuth();
const currentUserId = auth.currentUser?.uid || "";
useEffect(() => {
if (!auth.currentUser) {
showNotification("Please sign in to use broadcasting", "error");
    }
  }, []);
const showNotification = (message, type = "success") => {
setNotification({ show: true, message, type });
setTimeout(() => {
setNotification({ show: false, message: "", type: "" });
    }, 4000);
  };
// Fetch Users
useEffect(() => {
const profileRef = ref(database, "profileDashboard");
const unsubscribe = onValue(profileRef, (snapshot) => {
const data = snapshot.val();
if (data) {
const userList = Object.keys(data).map((userId) => ({
userId,
company_name: data[userId].company_name || "Unknown Company",
        }));
const sorted = userList.sort((a, b) =>
a.company_name.localeCompare(b.company_name)
        );
setUsers(sorted);
      } else {
setUsers([]);
      }
    });
return () => unsubscribe();
  }, []);
// Fetch Broadcasts
useEffect(() => {
if (!currentUserId) return;
const broadcastRef = ref(database, `broadcasts/${currentUserId}`);
const unsubscribe = onValue(broadcastRef, (snapshot) => {
const data = snapshot.val();
if (data) {
const list = Object.keys(data).map((id) => ({ id, ...data[id] }));
setBroadcasts(list);
      } else {
setBroadcasts([]);
      }
    });
return () => unsubscribe();
  }, [currentUserId]);
// ==================== NEW: AUTO CHECK & MARK EXPIRED (status boolean) ====================
// Runs immediately when page loads + every 60 seconds
useEffect(() => {
const checkAndMarkExpired = async () => {
const now = Date.now();
for (const b of broadcasts) {
// Only check sent broadcasts that are still ongoing (status === true)
if (b.status !== true) continue;
const sentTime = new Date(b.timestamp).getTime();
const validityMs = {
"1 day": 86400000,
"2 days": 172800000,
"1 week": 604800000,
"2 weeks": 1209600000,
"1 month": 2592000000,
        }[b.validity] || 172800000;
if (sentTime + validityMs < now) {
try {
// Mark as finished (status: false) in sender's node
await update(ref(database, `broadcasts/${currentUserId}/${b.id}`), {
status: false,
            });
// Also update in all recipients' nodes (so they also see it as finished)
if (b.recipients && b.recipients.length > 0) {
await Promise.all(
b.recipients.map((rid) =>
update(ref(database, `broadcasts/${rid}/${b.id}`), {
status: false,
                  })
                )
              );
            }
          } catch (err) {
console.error("Failed to mark broadcast as expired:", err);
          }
        }
      }
    };
// Initial check when page opens
checkAndMarkExpired();
// Check every minute
const interval = setInterval(checkAndMarkExpired, 60000);
return () => clearInterval(interval);
  }, [broadcasts, currentUserId]);
// Manual Delete (still completely removes)
const handleDelete = async (id, type) => {
if (!window.confirm(`Delete this ${type}?`)) return;
try {
const broadcast = broadcasts.find((b) => b.id === id);
await remove(ref(database, `broadcasts/${currentUserId}/${id}`));
if (broadcast?.recipients && broadcast.recipients.length > 0) {
await Promise.all(
broadcast.recipients.map((rid) =>
remove(ref(database, `broadcasts/${rid}/${id}`))
          )
        );
      }
showNotification(`${type} deleted successfully`);
    } catch (err) {
console.error("Delete error:", err);
showNotification("Delete failed", "error");
    }
  };
// Edit Draft
const handleEditDraft = (draft) => {
setHeading(draft.heading || "");
setMessage(draft.message || "");
setValidity(draft.validity || "2 days");
setRecipientType(
draft.recipients?.length === users.length ? "all" : "selected"
    );
setSelectedUsers(draft.recipients || []);
setEditingDraftId(draft.id);
// Handle existing image
if (draft.imageUrl) {
setExistingImageUrl(draft.imageUrl);
setImagePreview(draft.imageUrl);
setImageFile(null);
    } else {
setExistingImageUrl("");
setImagePreview("");
setImageFile(null);
    }
setActiveTab("compose");
showNotification("Editing draft...");
  };
const handleUserSelect = (userId) => {
setSelectedUsers((prev) =>
prev.includes(userId)
? prev.filter((id) => id !== userId)
: [...prev, userId]
    );
  };
// NEW: Image selection handler
const handleImageSelect = (e) => {
const file = e.target.files[0];
if (!file) return;
if (!file.type.startsWith("image/")) {
showNotification("Please select a valid image file", "error");
return;
    }
if (file.size > 5 * 1024 * 1024) {
showNotification("Image size must be less than 5MB", "error");
return;
    }
setImageFile(file);
setImagePreview(URL.createObjectURL(file));
setExistingImageUrl(""); // new image selected → clear old one
  };
// NEW: Remove selected / existing image
const removeImage = () => {
setImageFile(null);
setImagePreview("");
if (imagePreview && imagePreview.startsWith("blob:")) {
URL.revokeObjectURL(imagePreview);
    }
setExistingImageUrl(""); // also clear existing so it gets removed on save
  };
// NEW: Upload image to Firebase Storage
const uploadImageToStorage = async (broadcastId) => {
if (!imageFile) return null;
setUploadingImage(true);
try {
const storage = getStorage();
const fileName = `${Date.now()}-${imageFile.name}`;
const imageStorageRef = storageRef(
storage,
`broadcasts/${currentUserId}/${broadcastId}/${fileName}`
      );
const snapshot = await uploadBytes(imageStorageRef, imageFile);
const downloadURL = await getDownloadURL(snapshot.ref);
return downloadURL;
    } catch (err) {
console.error("Image upload error:", err);
showNotification("Failed to upload image", "error");
throw err;
    } finally {
setUploadingImage(false);
    }
  };
const validateForm = () => {
if (!heading.trim() || !message.trim()) {
showNotification("Heading and message are required", "error");
return false;
    }
if (recipientType === "selected" && selectedUsers.length === 0) {
showNotification("Select at least one recipient", "error");
return false;
    }
return true;
  };
const getRecipients = () =>
recipientType === "all" ? users.map((u) => u.userId) : selectedUsers;
// Save / Send Broadcast (now uses boolean status)
const saveBroadcast = async (action) => {
if (!validateForm()) return;
setLoading(true);
try {
const recipients = getRecipients();
let finalImageUrl = existingImageUrl;
// If user selected a new image, upload it
if (imageFile) {
const tempId = editingDraftId || "temp"; // will be replaced below
finalImageUrl = await uploadImageToStorage(tempId);
      }
const data = {
heading,
message,
validity,
senderId: currentUserId,
timestamp: new Date().toISOString(),
recipients,
imageUrl: finalImageUrl || "", // empty string = no image
      };
let broadcastId;
if (editingDraftId) {
// === Editing existing draft ===
broadcastId = editingDraftId;
if (action === "sent") {
// Send draft → mark as ongoing (status: true)
await update(ref(database, `broadcasts/${currentUserId}/${broadcastId}`), {
...data,
status: true,
          });
// Create / update same ID in all recipients
await Promise.all(
recipients.map((rid) =>
set(ref(database, `broadcasts/${rid}/${broadcastId}`), {
...data,
status: true,
              })
            )
          );
        } else {
// Save as draft again
await set(ref(database, `broadcasts/${currentUserId}/${broadcastId}`), {
...data,
status: "draft",
          });
        }
setEditingDraftId(null);
      } else {
// === New broadcast ===
const broadcastRef = push(ref(database, `broadcasts/${currentUserId}`));
broadcastId = broadcastRef.key;
const fullData = {
...data,
id: broadcastId,
status: action === "sent" ? true : "draft",
        };
// Save to sender
await set(ref(database, `broadcasts/${currentUserId}/${broadcastId}`), fullData);
// Save to all recipients with SAME ID
if (action === "sent") {
await Promise.all(
recipients.map((rid) =>
set(ref(database, `broadcasts/${rid}/${broadcastId}`), fullData)
            )
          );
        }
      }
// Reset form
setHeading("");
setMessage("");
setValidity("2 days");
setSelectedUsers([]);
setRecipientType("all");
setEditingDraftId(null);
setImageFile(null);
setImagePreview("");
setExistingImageUrl("");
showNotification(
action === "sent" ? "Broadcast sent successfully!" : "Draft saved!"
      );
setActiveTab(action === "sent" ? "sent" : "drafts");
    } catch (err) {
console.error(err);
showNotification("Operation failed", "error");
    } finally {
setLoading(false);
    }
  };
const filteredUsers = users.filter((u) =>
u.company_name.toLowerCase().includes(searchTerm.toLowerCase())
  );
// Only show ongoing broadcasts (status === true) and drafts
const sentBroadcasts = broadcasts.filter((b) => b.status === true);
const draftBroadcasts = broadcasts.filter((b) => b.status === "draft");
return (
<div className="broadcast-container">
{/* Notification */}
{notification.show && (
<div className={`broadcast-notification ${notification.type}`}>
{notification.message}
</div>
      )}
<div className="broadcast-header">
<h1>Broadcasting Dashboard</h1>
<p>Send messages to all or selected users</p>
</div>
<div className="tabs">
<button
className={activeTab === "compose" ? "active" : ""}
onClick={() => setActiveTab("compose")}
>
          Compose
</button>
<button
className={activeTab === "drafts" ? "active" : ""}
onClick={() => setActiveTab("drafts")}
>
          Drafts ({draftBroadcasts.length})
</button>
<button
className={activeTab === "sent" ? "active" : ""}
onClick={() => setActiveTab("sent")}
>
          Sent ({sentBroadcasts.length})
</button>
</div>
{/* ===================== COMPOSE TAB ===================== */}
{activeTab === "compose" && (
<div className="card">
<h2>{editingDraftId ? "Edit Draft" : "Compose New Broadcast"}</h2>
<div className="form-grid">
<div className="form-group">
<label>Heading *</label>
<input
value={heading}
onChange={(e) => setHeading(e.target.value)}
placeholder="Enter heading"
/>
</div>
<div className="form-group">
<label>Message *</label>
<textarea
rows={6}
value={message}
onChange={(e) => setMessage(e.target.value)}
placeholder="Write your message..."
/>
</div>
{/* NEW: Image Upload Section */}
<div className="form-group">
<label>Image (optional)</label>
<input
type="file"
accept="image/*"
onChange={handleImageSelect}
style={{ marginBottom: "8px" }}
/>
{imagePreview && (
<div style={{ marginTop: "8px" }}>
<img
src={imagePreview}
alt="Preview"
style={{
maxHeight: "200px",
maxWidth: "100%",
borderRadius: "8px",
border: "1px solid #ddd",
                    }}
/>
<button
onClick={removeImage}
style={{
marginTop: "8px",
background: "#e74c3c",
color: "white",
border: "none",
padding: "6px 12px",
borderRadius: "4px",
cursor: "pointer",
                    }}
>
                    Remove Image
</button>
</div>
              )}
</div>
<div className="form-row">
<div className="form-group">
<label>Validity</label>
<select
value={validity}
onChange={(e) => setValidity(e.target.value)}
>
<option value="1 day">1 day</option>
<option value="2 days">2 days</option>
<option value="1 week">1 week</option>
<option value="2 weeks">2 weeks</option>
<option value="1 month">1 month</option>
</select>
</div>
<div className="form-group full-width">
<label>Recipients</label>
<div className="recipient-tabs">
<label className="recipient-tab">
<input
type="checkbox"
checked={recipientType === "all"}
onChange={() => setRecipientType("all")}
/>
<span className="tab-label">
                      All Users <span className="counter">({users.length})</span>
</span>
</label>
<label className="recipient-tab">
<input
type="checkbox"
checked={recipientType === "selected"}
onChange={() => setRecipientType("selected")}
/>
<span className="tab-label">
                      Select Users{" "}
<span className="counter">({selectedUsers.length})</span>
</span>
</label>
</div>
</div>
</div>
{recipientType === "selected" && (
<div className="user-selector-card">
<div className="selector-header">
<input
type="text"
placeholder="Search companies..."
value={searchTerm}
onChange={(e) => setSearchTerm(e.target.value)}
/>
<div className="selector-actions">
<button
onClick={() =>
setSelectedUsers(users.map((u) => u.userId))
}
>
                      Select All
</button>
<button
onClick={() => setSelectedUsers([])}
className="clear"
>
                      Clear
</button>
</div>
</div>
<div className="user-list">
{filteredUsers.map((user) => (
<label key={user.userId} className="user-item">
<input
type="checkbox"
checked={selectedUsers.includes(user.userId)}
onChange={() => handleUserSelect(user.userId)}
/>
<div>
<div className="company">{user.company_name}</div>
<div className="id">
                          ID: {user.userId.slice(0, 10)}...
</div>
</div>
</label>
                  ))}
</div>
</div>
            )}
<div className="actions">
<button
onClick={() => saveBroadcast("sent")}
className="send"
disabled={loading || uploadingImage}
>
{loading || uploadingImage ? "Sending..." : "Send Broadcast"}
</button>
<button
onClick={() => saveBroadcast("draft")}
disabled={loading || uploadingImage}
>
                Save as Draft
</button>
{editingDraftId && (
<button
onClick={() => {
setEditingDraftId(null);
setHeading("");
setMessage("");
setSelectedUsers([]);
setRecipientType("all");
setImageFile(null);
setImagePreview("");
setExistingImageUrl("");
                  }}
>
                  Cancel Edit
</button>
              )}
</div>
</div>
</div>
      )}
{/* ===================== DRAFTS & SENT TABS ===================== */}
{(activeTab === "drafts" || activeTab === "sent") && (
<div className="broadcasts-grid">
{(activeTab === "drafts" ? draftBroadcasts : sentBroadcasts).map(
            (b) => (
<div key={b.id} className="broadcast-card">
<div className="card-header">
<h3>{b.heading}</h3>
<div className="actions">
{activeTab === "drafts" && (
<button
onClick={() => handleEditDraft(b)}
title="Edit"
style={{
background: "transparent",
border: "none",
cursor: "pointer",
padding: "4px",
color: "#3498db",
                        }}
>
<Pencil size={18} />
</button>
                    )}
<button
onClick={() =>
handleDelete(
b.id,
activeTab === "drafts" ? "draft" : "broadcast"
                        )
}
title="Delete"
style={{
background: "transparent",
border: "none",
cursor: "pointer",
padding: "4px",
color: "#e74c3c",
                      }}
>
<Trash2 size={18} />
</button>
</div>
</div>
<span className={`badge ${b.status}`}>
{b.status === "draft" ? "Draft" : "Sent"}
</span>
{/* Show image if exists */}
{b.imageUrl && (
<img
src={b.imageUrl}
alt="Broadcast"
style={{
width: "100%",
maxHeight: "180px",
objectFit: "cover",
borderRadius: "8px",
margin: "12px 0",
                    }}
/>
                )}
<p className="date">
{b.status === "draft" ? "Saved" : "Sent"}:{" "}
{new Date(b.timestamp).toLocaleString()}
</p>
<p className="message-preview">
{b.message.length > 120
                    ? b.message.substring(0, 120) + "..."
                    : b.message}
</p>
<div className="footer">
<span>Recipients: {b.recipients?.length || 0}</span>
<span>Validity: {b.validity}</span>
</div>
</div>
            )
          )}
{activeTab === "drafts" && draftBroadcasts.length === 0 && (
<div className="empty">No drafts yet</div>
          )}
{activeTab === "sent" && sentBroadcasts.length === 0 && (
<div className="empty">No active sent broadcasts</div>
          )}
</div>
      )}
</div>
  );
};
export default BroadcastingPage;
